import {
  IdSchema,
  type IdGenerator,
  type MonotonicClock,
  type WallClock,
} from "@hisani/core";
import { useEffect } from "react";

export type StopwatchSoundCue =
  "start" | "split" | "pause" | "resume" | "stop" | "finish" | "reset";

export type StopwatchSoundPlayer = (cue: StopwatchSoundCue) => Promise<void>;

const SWIPE_DELETE_HINT_STORAGE_KEY = "hisani.stopwatch.swipe-delete-hint-seen";

interface CueTone {
  readonly frequency: number;
  readonly offsetMilliseconds: number;
  readonly durationMilliseconds: number;
}

const CUE_TONES: Readonly<Record<StopwatchSoundCue, readonly CueTone[]>> = {
  start: [
    { frequency: 520, offsetMilliseconds: 0, durationMilliseconds: 70 },
    { frequency: 700, offsetMilliseconds: 75, durationMilliseconds: 90 },
  ],
  split: [{ frequency: 880, offsetMilliseconds: 0, durationMilliseconds: 85 }],
  pause: [
    { frequency: 440, offsetMilliseconds: 0, durationMilliseconds: 65 },
    { frequency: 330, offsetMilliseconds: 70, durationMilliseconds: 75 },
  ],
  resume: [
    { frequency: 440, offsetMilliseconds: 0, durationMilliseconds: 60 },
    { frequency: 620, offsetMilliseconds: 65, durationMilliseconds: 80 },
  ],
  stop: [
    { frequency: 330, offsetMilliseconds: 0, durationMilliseconds: 75 },
    { frequency: 220, offsetMilliseconds: 80, durationMilliseconds: 105 },
  ],
  finish: [
    { frequency: 523, offsetMilliseconds: 0, durationMilliseconds: 70 },
    { frequency: 659, offsetMilliseconds: 75, durationMilliseconds: 70 },
    { frequency: 784, offsetMilliseconds: 150, durationMilliseconds: 120 },
  ],
  reset: [{ frequency: 260, offsetMilliseconds: 0, durationMilliseconds: 110 }],
};

let stopwatchAudioContext: AudioContext | null = null;

export const browserClock: MonotonicClock = {
  nowMilliseconds: () => performance.now(),
};

export const browserWallClock: WallClock = {
  now: () => new Date(),
};

export const newBrowserId: IdGenerator = () =>
  IdSchema.parse(globalThis.crypto.randomUUID());

export function takeSwipeDeleteHint(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    if (window.localStorage.getItem(SWIPE_DELETE_HINT_STORAGE_KEY) !== null) {
      return false;
    }
    window.localStorage.setItem(SWIPE_DELETE_HINT_STORAGE_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

export const playStopwatchSound: StopwatchSoundPlayer = async (cue) => {
  if (typeof window === "undefined" || window.AudioContext === undefined) {
    return;
  }

  if (
    stopwatchAudioContext === null ||
    stopwatchAudioContext.state === "closed"
  ) {
    stopwatchAudioContext = new window.AudioContext();
  }
  if (stopwatchAudioContext.state === "suspended") {
    await stopwatchAudioContext.resume();
  }
  if (stopwatchAudioContext.state !== "running") {
    return;
  }

  const startAt = stopwatchAudioContext.currentTime + 0.005;
  for (const tone of CUE_TONES[cue]) {
    scheduleCueTone(stopwatchAudioContext, tone, startAt);
  }
};

function scheduleCueTone(
  context: AudioContext,
  tone: CueTone,
  cueStart: number,
): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const startsAt = cueStart + tone.offsetMilliseconds / 1_000;
  const endsAt = startsAt + tone.durationMilliseconds / 1_000;

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(tone.frequency, startsAt);
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(0.055, startsAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.addEventListener(
    "ended",
    () => {
      oscillator.disconnect();
      gain.disconnect();
    },
    { once: true },
  );
  oscillator.start(startsAt);
  oscillator.stop(endsAt + 0.01);
}

export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard !== undefined) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.append(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  textArea.remove();
  if (!copied) {
    throw new Error("Clipboard copy is unavailable.");
  }
}

export function useWakeLock(
  shouldHoldLock: boolean,
  unavailableMessage: string,
  setNotice: (message: string | null) => void,
): void {
  useEffect(() => {
    if (!shouldHoldLock || navigator.wakeLock === undefined) {
      if (shouldHoldLock) {
        setNotice(unavailableMessage);
      }
      return;
    }

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const requestLock = async () => {
      const nextSentinel = await navigator.wakeLock.request("screen");
      if (cancelled) {
        await nextSentinel.release();
        return;
      }
      sentinel = nextSentinel;
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        sentinel?.released !== false
      ) {
        requestLock().catch(() => setNotice(unavailableMessage));
      }
    };

    requestLock().catch(() => setNotice(unavailableMessage));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (sentinel !== null && !sentinel.released) {
        sentinel.release().catch(() => setNotice(unavailableMessage));
      }
    };
  }, [setNotice, shouldHoldLock, unavailableMessage]);
}
